import type { ReactNode } from 'react';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Badge, Button, Card, CardBody, Spinner } from '@fluxora/ui';

import { useAgreementPreviewQuery } from '../api/agreement.queries';
import { BffError } from '../api/bff-client';
import { useElapsedMs } from '../hooks/useElapsedMs';
import { useObjectUrl } from '../hooks/useObjectUrl';
import {
  classifyPreviewFailure,
  pdfViewerSrc,
  previewProgressKey,
} from '../services/agreement-preview.service';

/**
 * E-SF18 — THE CONTRACT, RENDERED WHERE IT IS SIGNED.
 *
 * The customer's real, unsigned agreement PDF, shown inline and scrollable on the
 * sign step, before the acknowledgements and the signature. It replaces three
 * plain-text disclosure blocks that described the contract without ever showing
 * it: *"we don't sign just below ticking the mark — instead we render the PDF
 * preview there, where he can see his contract"*.
 *
 * ── THE DOCUMENT IS FETCHED, NOT FRAMED ────────────────────────────────────
 * `<iframe src="/bff/sign/agreement-preview.pdf">` would have been one line, and
 * would have handed the request to the browser — which means handing it the
 * FAILURE too. A 503 becomes native error chrome the app cannot see, cannot
 * explain and cannot offer a retry from, and a slow render is indistinguishable
 * from a dead page. `SignedAgreementDownload` already learned this when it stopped
 * being an `<a download>`; this reuses the same mechanism (fetch the bytes, build
 * an object URL) so we own the pending state, the typed error and the retry, while
 * the browser's own PDF viewer still does the rendering.
 *
 * ── NO EXTERNAL RENDERER ───────────────────────────────────────────────────
 * The viewer is the browser's, via `<object>` on a blob URL. Nothing is loaded
 * from a CDN: a regulatory document must not depend on a third-party host being
 * up, or on that host learning which contracts our customers are reading.
 *
 * ── FIVE STATES, AND NEVER AN INDEFINITE SPINNER ───────────────────────────
 * idle (no enrollment yet) / loading (with progress copy that ESCALATES on a
 * clock — see `agreement-preview.service`) / error (typed, with a retry when a
 * retry can help) / empty (bytes arrived but this browser cannot draw them — the
 * `<object>` fallback) / success. QA measured the SIGNED render at 279s cold; the
 * unsigned preview is the cheap path, but the rule stands: the wait is named, not
 * hidden.
 *
 * ── THE TEXT DISCLOSURES REMAIN, ONE LEVEL DOWN ────────────────────────────
 * A PDF in an `<object>` is not dependable for a screen reader. The caller passes
 * the plain-text EFL/TOS/YRAC as `textAlternative`, rendered in a `<details>`
 * beneath the viewer, so the review obligation is satisfiable without a PDF
 * renderer at all (E-SF18 §8, US-SF18.6).
 */

export interface ContractPreviewProps {
  /** The opaque, session-scoped premise handle. */
  readonly candidateId: string | undefined;
  /** The tariff being signed. Both halves are needed to resolve the DRAFT quote. */
  readonly planId: string | undefined;
  /**
   * The accessible text alternative (the EFL/TOS/YRAC blocks). Rendered collapsed
   * beneath the viewer; NOT a replacement for it.
   */
  readonly textAlternative: ReactNode;
  /**
   * AC2 — when false, the document is badged as a PREVIEW and never presented as a
   * live, publishable PUCT §25.475 EFL. Passed in (not read from env) so the gate
   * is trivially testable in both positions, matching `EflDisclosure`.
   */
  readonly publishEnabled: boolean;
  /**
   * How much vertical room the viewer may take.
   *
   *   'inline'  — a viewport-relative block in a single scrolling column (mobile,
   *               and any narrow layout). Keeps its own floor so a short landscape
   *               window still shows more than a sliver.
   *   'column'  — the viewer OWNS a full-height reading column beside the signing
   *               rail (desktop). It stretches to whatever the column gives it.
   *
   * ⚠️ A PROP RATHER THAN A MEDIA QUERY INSIDE THE VIEWER. The decision belongs to
   * whoever built the two-column grid, because only that caller knows whether this
   * component is in a column at all. Baking a `lg:` height in here would make the
   * viewer silently wrong the first time it is reused outside the sign step.
   */
  readonly layout?: 'inline' | 'column';
}

export function ContractPreview({
  candidateId,
  planId,
  textAlternative,
  publishEnabled,
  layout = 'inline',
}: ContractPreviewProps): JSX.Element {
  const { t } = useTranslation();

  const preview = useAgreementPreviewQuery({ candidateId, planId });
  const objectUrl = useObjectUrl(preview.data?.blob);
  // Timed from the moment this fetch started, so a retry is timed from the retry.
  const elapsedMs = useElapsedMs(preview.isFetching);

  // ⚠️ THE PARENT DOES NOT LEARN THIS THROUGH A CALLBACK. The sign control is
  // gated on the same fact this component renders, and the honest way to share it
  // is to share the QUERY: `SignSurface` calls `useAgreementPreviewQuery` with the
  // same key and reads `isSuccess` (TanStack serves both readers from one cache
  // entry and one in-flight request). An `onPresentedChange` prop would have meant
  // either a setState during render — which React refuses across components — or
  // an effect, leaving one commit in which the button disagrees with what is on
  // screen. Neither is worth a prop.
  const presented = preview.isSuccess;

  const failure =
    preview.error === null || preview.error === undefined
      ? null
      : classifyPreviewFailure(
          preview.error instanceof BffError ? preview.error.status : undefined,
          preview.error instanceof BffError ? preview.error.problem?.type : undefined,
        );

  // ── HOW TALL THE READING SURFACE IS ────────────────────────────────────────
  //
  // In 'column' the section fills its grid cell, the card takes the leftover space
  // and the `<object>` fills the card — but ONLY from `lg` up, because that is the
  // only breakpoint at which the caller actually gives the column a height. Below
  // it the two-column grid collapses to normal block flow, nothing constrains the
  // chain, and `flex-1` with no basis resolves to CONTENT height: a first pass of
  // this rendered the contract 270 × **152px** on a 390px phone. So every flex
  // class here is `lg:`-scoped and the mobile height falls back to exactly what
  // 'inline' has always used.
  //
  // `min-h-0` is load-bearing, not defensive: a flex child's default `min-height:
  // auto` refuses to shrink below its content, so without it a multi-page PDF
  // pushes the column past the bottom of the viewport and the sticky column stops
  // being sticky.
  const isColumn = layout === 'column';
  const viewportHeight = 'h-[70vh] min-h-[24rem]';
  const sectionClass = isColumn
    ? 'flex flex-col gap-3 lg:min-h-0 lg:flex-1'
    : 'flex flex-col gap-3';
  const cardClass = isColumn ? 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col' : '';
  const bodyClass = isColumn
    ? 'flex flex-col gap-3 p-0 lg:min-h-0 lg:flex-1 lg:overflow-auto'
    : 'flex flex-col gap-3 p-0';
  const viewerClass = isColumn
    ? `${viewportHeight} w-full rounded-md border border-border bg-surface lg:h-auto lg:min-h-0 lg:flex-1`
    : `${viewportHeight} w-full rounded-md border border-border bg-surface`;

  return (
    <section className={sectionClass} aria-label={t('shop.sign.document.heading')}>
      {/* The header cluster ("Your contract" + badge + intro sentence) was
          retired: the document below IS the content, and the preview badge
          survives as `data-testid` state on the section for the tests. */}
      {publishEnabled ? null : (
        <span className="hidden" data-testid="contract-preview-badge" aria-hidden="true" />
      )}
      <Card variant="glass" padding="md" className={cardClass}>
        <CardBody className={bodyClass}>
          {/* ── idle ── no enrollment resolved yet, so there is nothing to draw. */}
          {preview.isPending && !preview.isFetching ? (
            <p className="text-sm text-foreground/60">{t('shop.sign.document.idle')}</p>
          ) : null}

          {/* ── loading ── named, escalating progress. Never a bare spinner. */}
          {preview.isFetching ? (
            <div
              className="flex min-h-[12rem] flex-col items-center justify-center gap-3 py-8"
              role="status"
              aria-live="polite"
            >
              {/* ⚠️ THE SPINNER IS HIDDEN FROM ASSISTIVE TECH, DELIBERATELY.
                  `Spinner` carries its own `role="status"` and an sr-only label,
                  so leaving it visible to AT puts TWO live regions inside this
                  one — a screen reader then hears the spinner's fixed label
                  competing with the escalating progress sentence, and neither
                  lands. The wrapper owns the announcement; the spinner is
                  decoration for people who can see it. */}
              <span aria-hidden="true">
                {/* An EMPTY label, paired with the `aria-hidden` above. `Spinner`
                    renders its label into an `sr-only` span; a non-empty one would
                    duplicate the progress sentence in the DOM even though AT
                    cannot reach it — which is how a test starts finding the same
                    copy twice and a reader would, if the hiding were ever
                    removed, hear it twice. */}
                <Spinner size="lg" label="" />
              </span>
              <p className="max-w-md text-center text-sm text-foreground/70">
                {t(previewProgressKey(elapsedMs))}
              </p>
            </div>
          ) : null}

          {/* ── error ── typed, with a retry only where a retry can help. */}
          {failure !== null && !preview.isFetching ? (
            <Alert
              variant={failure.canRetry ? 'warning' : 'danger'}
              title={t(failure.titleKey)}
              role={failure.canRetry ? 'status' : 'alert'}
            >
              <span className="flex flex-col items-start gap-2">
                <span>{t(failure.bodyKey)}</span>
                {failure.canRetry ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      void preview.refetch();
                    }}
                  >
                    {t('shop.sign.document.retry')}
                  </Button>
                ) : null}
              </span>
            </Alert>
          ) : null}

          {/* ── success ── the real document, inline and scrollable. */}
          {presented && !preview.isFetching ? (
            <>
              {objectUrl === null ? (
                // ── empty ── bytes in hand, but this host cannot mint an object
                // URL (a non-browser environment). Say so honestly rather than
                // rendering a blank frame that looks like an empty contract.
                <Alert variant="info" title={t('shop.sign.document.noViewer.title')} role="status">
                  {t('shop.sign.document.noViewer.body')}
                </Alert>
              ) : (
                <object
                  // Fit-to-width, thumbnails collapsed — see `pdfViewerSrc`. The
                  // viewer was opening the contract at 50% zoom behind a thumbnail
                  // rail, spending ~40% of the reading column on chrome.
                  data={pdfViewerSrc(objectUrl) ?? objectUrl}
                  type="application/pdf"
                  // Sized by `layout` (see the prop): viewport-relative with an
                  // absolute floor when inline, or filling its reading column on
                  // desktop. This used to be a hard-coded 70vh inside a 672px page
                  // container, which drew a legal contract 493px wide on a 1375px
                  // screen with ~700px of empty page either side. The PDF viewer
                  // supplies its own scrolling either way.
                  className={viewerClass}
                  aria-label={t('shop.sign.document.viewer.label')}
                  data-testid="contract-preview-object"
                >
                  {/* `<object>` children render ONLY when the resource cannot be
                      displayed — the correct place for the fallback, and the case
                      most mobile browsers actually take for an inline PDF. */}
                  <div className="flex flex-col items-start gap-2 p-4">
                    <p className="text-sm text-foreground/80">
                      {t('shop.sign.document.fallback.body')}
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        window.open(objectUrl, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      {t('shop.sign.document.openInNewTab')}
                    </Button>
                  </div>
                </object>
              )}

              {/* Always available, not only in the fallback: on a phone the inline
                  viewer is cramped even when it works, and some customers simply
                  prefer their own PDF reader. */}
              {objectUrl === null ? null : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-fit"
                  onClick={() => {
                    window.open(objectUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  {t('shop.sign.document.openInNewTab')}
                </Button>
              )}
            </>
          ) : null}
        </CardBody>
      </Card>

      {/* The accessible alternative. Collapsed, because the PDF is the document —
          but present, because a PDF in an `<object>` is not dependable for a screen
          reader and the review obligation must be satisfiable without one. */}
      <details className="rounded-md border border-border p-3">
        <summary className="cursor-pointer text-sm font-medium text-foreground">
          {t('shop.sign.document.textVersion.summary')}
        </summary>
        <div className="mt-3">
          <p className="mb-3 text-xs text-foreground/60">
            {t('shop.sign.document.textVersion.note')}
          </p>
          {textAlternative}
        </div>
      </details>
    </section>
  );
}
